"use client";

import { useEffect, useState } from "react";

import Carousel from "../Carousel";
import HighlightsPlaylist from "../HighlightsPlaylist/HighlightsPlaylist";
import { Project } from "@/types";
import { ProjectInfo } from "../../../../../APITypes/ProjectInfo.cs";
import { ProjectStreamData } from "../../../../../APITypes/ProjectStreamData.cs";
import PlayerOverlays from "../../../shared/PlayerOverlays";
import type { PublicInvestigation } from "@/lib/public-investigation";
import type { PublicSessionNotes } from "@/lib/public-notes";
import type { PublicSegmentKinds } from "@/lib/public-segments";
import { toRecordingClock } from "@/lib/recording-clock";
import TimeLine from "../Timeline/TimeLine";
import { useDataContext } from "../../../../../app/_context";

export default function PublicPageComponent(props: {
  pid: string;
  setURLcurrentIndex: number;
  data: ProjectStreamData;
  meta: ProjectInfo;
  widgetMode: boolean;
  baseUrl: string;
  linkSource: string;
  iframeHtml: string;
  /** Public session notes from stone-sync-api, keyed by session id. */
  notes?: PublicSessionNotes;
  /** The desktop app's investigation manifest from stone-sync-api. */
  investigation?: PublicInvestigation | null;
  /** What each segment is (shown work vs sped-up research), from stone-sync-api. */
  segmentKinds?: PublicSegmentKinds;
}) {
  // CONTEXT & STATES /////////
  const {
    currentSessionIndex,
    setCurrentPlaylist,
    isAudioMuted,
    currentPlaylist,
    isHighlightsOnlyMode,
    setCurrentProjectInfo,
    videoJsOptions,
    setVideoJsOptions,
    videoCanplay,
  } = useDataContext();

  const isVirginState = true;
  const hasContent = Boolean(videoCanplay && currentPlaylist?.length > 0);
  // A session with no highlights stream (an imported video) plays in full
  // rather than not at all — same fallback as the data context.
  const currentSource = isHighlightsOnlyMode
    ? currentPlaylist[currentSessionIndex]?.highlightsOnlyStream ??
      currentPlaylist[currentSessionIndex]?.fullStream
    : currentPlaylist[currentSessionIndex]?.fullStream;
  const highlightsArray =
    currentPlaylist[currentSessionIndex]?.highlights || [];
  const watermark = props.investigation?.watermark ?? null;
  const gatedPreview = props.investigation?.gatedPreview ?? null;
  const currentItem = currentPlaylist[currentSessionIndex];
  const overlay =
    watermark || gatedPreview ? (
      <PlayerOverlays
        watermark={watermark}
        gatedPreview={gatedPreview}
        recordingTimeMs={(playerMs) =>
          toRecordingClock(
            playerMs,
            currentItem?.segments ?? [],
            isHighlightsOnlyMode && Boolean(currentItem?.highlightsOnlyStream)
          )
        }
      />
    ) : undefined;

  const [mobileBump, setMobileBump] = useState("");

  // EFFECTS //////////////////////////////////////////////////////////////////////
  // Initialize playlist and set up video options

  useEffect(() => {
    if (props.data.sessionPlaylist && props.data.sessionPlaylist.length > 0) {
      setCurrentPlaylist(props.data.sessionPlaylist);
    }
  }, [props.data.sessionPlaylist]);

  // Update content and source based on the current playlist
  useEffect(() => {
    if (currentPlaylist?.length > 0) {
      // Ensure currentSource exists before setting it
      if (currentSource) {
        setVideoJsOptions([
          {
            src: currentSource.src || "",
            type: currentSource.type || "application/x-mpegURL",
          },
        ]);
      } else {
        setVideoJsOptions([]); // Clear video options if no valid source
      }
    }
  }, [currentPlaylist, currentSessionIndex, currentSource]);

  useEffect(() => {
    setCurrentProjectInfo(props.meta);
  }, [props.meta]);

  const ImageForOpenGraphRender = () => {
    if (currentPlaylist?.length > 0) {
      return (
        <img
          src={currentPlaylist[0].poster}
          width="300px"
          style={{
            position: "absolute",
            opacity: 0,
            top: "33vh",
            left: "50%",
            zIndex: -5,
            pointerEvents: "none",
          }}
        />
      );
    }
    return null;
  };

  return (
    <div className="h-full">
      {ImageForOpenGraphRender()}
      <div className="relative flex flex-col justify-between flex-1 h-full top-0 bottom-0 left-0 right-0">
        <TimeLine
          pid={props.pid}
          skeleton={!hasContent}
          hasContent={hasContent}
          meta={props.meta}
          widgetMode={props.widgetMode}
        />
        <div className="flex flex-col md:flex-row w-full h-full overflow-hidden">
          <Carousel
            pid={props.pid}
            videoJsOptions={videoJsOptions}
            highlightsArray={highlightsArray}
            playList={currentPlaylist}
            sessionPlaylist={props?.data?.sessionPlaylist}
            setHighlightsArray={() => null}
            widgetMode={props.widgetMode}
            isMuted={isAudioMuted}
            isVirginState={isVirginState}
            setMobileBump={setMobileBump}
            meta={props.meta}
            mobileBump={mobileBump}
            bookLink={props.meta?.merchandiseLink2}
            patreonLink={props.meta?.socialAccount1}
            twitterLink={props.meta?.socialAccount2}
            playerOverlay={overlay}
          />

          <HighlightsPlaylist
            meta={props.meta}
            pid={props.pid}
            widgetMode={props.widgetMode}
            notes={props.notes}
            investigation={props.investigation}
            segmentKinds={props.segmentKinds}
          />
        </div>
      </div>
    </div>
  );
}
